/**
 * /el/guides/synergates-kai-pliromes-gamou
 *
 * The Greek counterpart of `how-to-track-wedding-vendors-payments.en.js` — the
 * operational half of the Greek planning cluster.
 *
 * ── The differentiator, which is the whole piece ─────────────────────────
 * The chain is the English article's, stated once and then walked:
 *
 *   συνεργάτης → κατάσταση → συμφωνητικό → τι περιλαμβάνει → δόσεις →
 *   πληρωμένα → υπόλοιπο → επόμενη ενέργεια → τελική επιβεβαίωση
 *
 * And four things are kept deliberately apart, because conflating them is the
 * defect the article exists to fix: παρακολούθηση συνεργατών, παρακολούθηση
 * συμφωνητικών, παρακολούθηση πληρωμών, and προϋπολογισμός — which is none of
 * the three and gets a section saying so. An edit that lets the budget and the
 * vendor tracker blur back together removes the reason this article exists.
 *
 * ── Greek rather than translated ─────────────────────────────────────────
 *  - **Το συμφωνητικό.** The article's central instruction is to enter dates
 *    and amounts from the agreement you actually signed. That instruction is
 *    MORE load-bearing here, not less, because a Greek wedding is often booked
 *    on a handshake and a deposit, with the paperwork thinner than the English
 *    original assumes. So the section says plainly that a verbal agreement is
 *    still an agreement worth writing down, and asks for written confirmation
 *    where money, hours or headcount move — without ever telling the reader
 *    what the law says.
 *  - **Οι λέξεις.** Προκαταβολή, δόσεις, εξόφληση, απόδειξη, τιμολόγιο,
 *    κατάθεση. These are the words on a Greek couple's bank app and in their
 *    inbox, and they are what the tracker's columns are named after.
 *  - **Η αμοιβή σε μετρητά.** Not moralised about and not encouraged; the
 *    article's only interest is that a payment nobody recorded is a payment
 *    that will be argued about, so it says to write down what was paid, when,
 *    and how, whatever the method.
 *
 * ── The hedges, which are load-bearing ───────────────────────────────────
 * Payment terms are contractual, not conventional, and the article may never
 * imply otherwise:
 *
 *   - No percentage attached to any vendor type. Not venues, not photographers.
 *   - No universal number of instalments. The three-payment illustration is
 *     followed immediately by a sentence saying three is not a standard.
 *   - No universal final-payment deadline; work backwards from the earliest
 *     date in your own agreements.
 *   - «Βάλτε τις ημερομηνίες και τα ποσά από το συμφωνητικό που υπογράψατε»
 *     is stated twice, because it is the claim this guide most needs to get
 *     right.
 *
 * Not legal advice, and a note says so: the guide records what was agreed, it
 * does not interpret it. No tipping conventions, nothing asserted as law.
 *
 * ── The claims this guide is allowed to make ─────────────────────────────
 * All in the closing section, all already published on /el/wedding-planning:
 * the categories, the vendor card with its status and activity history, a cost
 * with a label, an amount, a due date and its vendor, the four totals with
 * overdue payments flagged, dated items on a calendar, documents filed against
 * the vendor they came from, and Éros reading the space and making a change
 * when asked.
 *
 * And four denials, each a real product boundary: Agapaé is not a vendor
 * directory and contacts, books and pays nobody; it sends no reminders about
 * vendor payments; it is not accounting or invoicing software; and it does not
 * stand in for the signed agreements or the couple's own record of what they
 * have paid. The reminder denial stays scoped to VENDOR payments — Stripe
 * mails about the couple's own Agapaé subscription do go out, so an unqualified
 * version of it would be false.
 *
 * No copy in this file contains "&", "<" or ">".
 */

const howToTrackWeddingVendorsPaymentsEl = {
  slug: "synergates-kai-pliromes-gamou",

  title: "Συνεργάτες και πληρωμές γάμου: πώς να τα παρακολουθείτε",
  metaTitle: "Συνεργάτες και πληρωμές γάμου: πώς να τα παρακολουθείτε | Agapaé",
  metaDescription:
    "Πώς να κρατάτε συνεργάτες, συμφωνητικά, προκαταβολές, δόσεις και υπόλοιπα σε ένα σημείο, ώστε να ξέρετε τι είναι κλεισμένο, τι πληρωμένο και τι εκκρεμεί.",

  eyebrow: "Οργάνωση γάμου",

  hero: {
    src: "/assets/images/bride_shoes.jpg",
    alt: "Ασπρόμαυρη φωτογραφία όπου κάποιος γονατίζει για να δέσει το λουράκι στο κεντημένο παπούτσι της νύφης, ενώ εκείνη στέκεται με το νυφικό της, με μόνο τα χέρια και τον ποδόγυρο στο κάδρο",
    width: 3000,
    height: 2000,
  },

  intro: [
    `Ένας γάμος δεν έρχεται ως ένας λογαριασμός. Έρχεται ως δώδεκα ξεχωριστές συμφωνίες, με τον δικό της άνθρωπο η καθεμία, τη δική της τιμή, τις δικές της ημερομηνίες και τη δική της άποψη για το πότε θέλει να πληρωθεί.`,
    `Η δυσκολία σπάνια είναι ο αριθμός των συνεργατών. Είναι ότι όλα όσα τους αφορούν καταλήγουν αλλού: η τιμή σε ένα email, η προκαταβολή σε μια κίνηση λογαριασμού, η ώρα άφιξης σε μια συνομιλία, οι όροι ακύρωσης σε ένα χαρτί που δεν άνοιξε ξανά κανείς από τότε που υπογράφηκε.`,
    `Αυτός ο οδηγός είναι για την καταγραφή που το λύνει: μία εγγραφή ανά συνεργάτη, τη συμφωνία από πίσω, τις πληρωμές που βγαίνουν από αυτήν και την κατάσταση που σας λέει τι χρειάζεται προσοχή αυτή την εβδομάδα.`,
  ],

  sections: [
    {
      heading: "Μία λίστα συνεργατών",
      content: [
        `Η λίστα δικαιώνεται την πρώτη φορά που απαντά σε κάτι που αλλιώς θα ψάχνατε. Έξι τέτοιες ερωτήσεις:`,
        {
          type: "list",
          items: [
            `Ποιους έχουμε κλείσει πραγματικά και ποιοι είναι ακόμη προσφορές;`,
            `Τι ακριβώς συμφωνήσαμε με τον καθένα;`,
            `Πόσο θα κοστίσει συνολικά ο καθένας;`,
            `Πόσα έχουμε ήδη πληρώσει;`,
            `Πόσα εκκρεμούν;`,
            `Ποια είναι η επόμενη πληρωμή και πότε πέφτει;`,
          ],
        },
        `Από μνήμης, οι απαντήσεις είναι περίπου σωστές — που είναι διαφορετικό πράγμα από σωστές. Οι τέσσερις τελευταίες δεν απαντιούνται καθόλου χωρίς ένα σημείο που να τις προσθέτει.`,
        `Μια λίστα που δουλεύει κρατά, για κάθε συνεργάτη:`,
        {
          type: "list",
          items: [
            `το όνομα ή την επωνυμία`,
            `το κομμάτι του γάμου που αναλαμβάνει`,
            `το άτομο με το οποίο μιλάτε πραγματικά`,
            `τηλέφωνο και email`,
            `την κατάσταση του κλεισίματος`,
            `αν υπάρχει υπογεγραμμένο συμφωνητικό και πού βρίσκεται`,
            `το συνολικό συμφωνημένο ποσό`,
            `τι έχει πληρωθεί μέχρι τώρα`,
            `το υπόλοιπο`,
            `την επόμενη πληρωμή και την ημερομηνία της`,
            `μια σημείωση για το τι περιμένετε`,
          ],
        },
        `Δύο πίνακες, μία σχέση: μια γραμμή ανά συνεργάτη με αυτά τα πεδία, και από κάτω μια γραμμή ανά πληρωμή με τον συνεργάτη, την περιγραφή, το ποσό, την ημερομηνία και την κατάστασή της. Κάθε σύνολο στη γραμμή του συνεργάτη πρέπει να βγαίνει από τις γραμμές των πληρωμών.`,
        `Δεν χρειάζεται συγκεκριμένο εργαλείο: ένα Excel, ένα εργαλείο οργάνωσης ή ένα κοινό έγγραφο δουλεύουν όλα. Σημασία έχει να υπάρχει ένα, να ενημερώνεται όταν αλλάζει κάτι και όχι όταν το θυμηθείτε, και να προσθέτετε στήλες όταν τις χρειαστείτε και όχι όλες μαζί από την αρχή.`,
      ],
    },
    {
      heading: "Μία εγγραφή ανά συνεργάτη",
      content: [
        `Η μονάδα δεν είναι η πληρωμή ούτε η κατηγορία. Είναι ο συνεργάτης. Ανοίξτε εγγραφή από τη στιγμή που κάποιος γίνεται πραγματική πιθανότητα, ώστε η προσφορά του Μαρτίου να βρίσκεται ακόμη τον Σεπτέμβριο.`,
        `Οι γάμοι συνήθως περιλαμβάνουν κάποιον συνδυασμό από:`,
        {
          type: "list",
          items: [
            `τον χώρο ή το κτήμα`,
            `το catering, όπου δεν το δίνει ο χώρος`,
            `τη φωτογράφιση`,
            `τη βιντεοσκόπηση`,
            `τα λουλούδια και τη διακόσμηση`,
            `τη μουσική ή το DJ`,
            `wedding planner ή συντονιστή της ημέρας`,
            `μαλλιά και μακιγιάζ`,
            `τις μετακινήσεις`,
            `την τούρτα`,
            `ενοικιάσεις, όπως έπιπλα, σερβίτσια ή φωτισμό`,
            `τα προσκλητήρια και τα έντυπα`,
          ],
        },
        `Κανένας γάμος δεν χρειάζεται όλα. Οι κατηγορίες απλώς ομαδοποιούν· η εγγραφή είναι το ζητούμενο, και μαζεύει με τη σειρά:`,
        {
          type: "steps",
          items: [
            `Προσθέτετε τον συνεργάτη, με κατηγορία και επαφή.`,
            `Τον μετακινείτε σε καταστάσεις καθώς προχωρά η συζήτηση.`,
            `Μόλις κλείσει, καταγράφετε τη συμφωνία: τι καλύπτει και τι κοστίζει.`,
            `Γράφετε τι ακριβώς περιλαμβάνει, σε υπηρεσίες και ποσότητες.`,
            `Βγάζετε από τη συμφωνία τις πληρωμές και τις καταχωρείτε όλες.`,
            `Σημειώνετε κάθε πληρωμή ως πληρωμένη όταν όντως ολοκληρωθεί.`,
            `Το υπόλοιπο είναι το συμφωνημένο σύνολο μείον όσα έχουν πληρωθεί.`,
            `Μια επόμενη ενέργεια κάθεται πάνω στην εγγραφή όποτε το επόμενο βήμα είναι δικό σας.`,
            `Τις τελευταίες εβδομάδες, επιβεβαιώνετε ολόκληρη την εγγραφή με τον συνεργάτη.`,
          ],
        },
      ],
    },
    {
      heading: "Πού βρίσκεται ο καθένας",
      content: [
        `Μια τιμή χωρίς κατάσταση δίπλα της δεν είναι πληροφορία. Δύο συνεργάτες με το ίδιο ποσό βρίσκονται σε εντελώς διαφορετική θέση, αν ο ένας έχει υπογράψει και ο άλλος δεν απαντά εδώ και εβδομάδες.`,
        `Μια απλή πορεία αρκεί:`,
        {
          type: "steps",
          items: [
            `Επικοινωνία. Μιλήσατε· τίποτα δεν έχει συμφωνηθεί.`,
            `Προσφορά. Έχετε τιμή, και είναι ακόμη δική σας απόφαση.`,
            `Κλεισμένος. Συμφωνήσατε να συνεργαστείτε.`,
            `Υπογεγραμμένο συμφωνητικό. Η συμφωνία υπάρχει γραπτά, και από τις δύο πλευρές.`,
            `Σε εξέλιξη. Κλεισμένος, μερικώς πληρωμένος, η δουλειά τρέχει.`,
            `Ολοκληρωμένος. Όλα συμφωνημένα, όλα πληρωμένα, κάθε λεπτομέρεια επιβεβαιωμένη.`,
          ],
        },
        `Προσαρμόστε τις στον τρόπο που δουλεύετε· σημασία έχει να τις εφαρμόζετε το ίδιο, ώστε πέντε συνεργάτες σημειωμένοι ως κλεισμένοι να είναι πράγματι στην ίδια θέση.`,
        `Κρατήστε επίτηδες ανοιχτή την απόσταση ανάμεσα στο «κλεισμένος» και στο «υπογεγραμμένο»: ένα προφορικό ναι και ένα γραπτό συμφωνητικό συμπεριφέρονται πολύ διαφορετικά αργότερα.`,
      ],
    },
    {
      heading: "Καταγράψτε τη συμφωνία, όχι μόνο την τιμή",
      content: [
        `Η τιμή είναι το λιγότερο χρήσιμο πράγμα σε μια συμφωνία γάμου: είναι το κομμάτι που ούτως ή άλλως θα θυμόσασταν. Τα υπόλοιπα είναι αυτά που θα προσπαθείτε να ανασυνθέσετε τον τελευταίο μήνα.`,
        `Μόλις κλείσει ένας συνεργάτης, κρατήστε στην εγγραφή του:`,
        {
          type: "list",
          items: [
            `το συμφωνημένο ποσό, όπως ακριβώς διατυπώνεται`,
            `τι περιλαμβάνει αυτό το ποσό, υπηρεσία προς υπηρεσία`,
            `πόσα άτομα, μερίδες ή τεμάχια καλύπτει`,
            `ώρα άφιξης και στησίματος, και πόσες ώρες κρατά η παρουσία τους`,
            `τι χρειάζονται από τον χώρο: πρόσβαση, ρεύμα, επιφάνεια για το στήσιμό τους`,
            `τις ημερομηνίες και τις ώρες που έχετε συμφωνήσει`,
            `τι θα σας παραδώσουν μετά, και περίπου πότε`,
            `αν η ομάδα τους χρειάζεται φαγητό, και για πόσους`,
            `τους όρους πληρωμής, με τα λόγια της ίδιας της συμφωνίας`,
            `τι κοστίζει η υπερωρία, αν η βραδιά τραβήξει`,
            `τους όρους ακύρωσης και αναβολής`,
            `ό,τι ιδιαίτερο έχει αυτός ο συνεργάτης και δεν θα σκεφτόσασταν να ρωτήσετε δεύτερη φορά`,
            `τις ερωτήσεις που κάνατε και δεν έχουν ακόμη απαντηθεί`,
          ],
        },
        {
          type: "note",
          text: `Αυτό είναι καταγραφή του τι συμφωνήσατε, όχι ερμηνεία του. Αν ένας όρος έχει σημασία και δεν είστε σίγουροι τι σημαίνει, ρωτήστε τον συνεργάτη ή πάρτε κατάλληλη συμβουλή.`,
        },
        `Και μια ελληνική ιδιαιτερότητα που αξίζει να ειπωθεί: αρκετές συμφωνίες κλείνονται με μια κουβέντα και μια προκαταβολή, χωρίς πολλά χαρτιά. Δεν παύουν να είναι συμφωνίες. Γράψτε τις όπως τις συμφωνήσατε, και ζητήστε γραπτή επιβεβαίωση — έστω ένα email — όπου παίζονται χρήματα, ώρες ή αριθμός ατόμων.`,
        `Το τελευταίο σημείο της λίστας είναι αυτό που λείπει πιο συχνά και αξίζει περισσότερο. Μια ερώτηση γραμμένη απαντιέται· μια ερώτηση που κουβαλάτε τη θυμάστε το πρωί του γάμου.`,
        {
          type: "p",
          text: [
            `Αρκετά από αυτά εξαρτώνται από τον τελικό αριθμό και όχι από τον συνεργάτη: μερίδες, καρέκλες, θέσεις σε αυτοκίνητο. Αυτά βγαίνουν από τη λίστα σας και τις απαντήσεις της, και το `,
            {
              text: "Λίστα καλεσμένων γάμου: πώς να την οργανώσετε",
              href: "/guides/how-to-organize-wedding-guest-list",
            },
            ` καλύπτει το πώς κρατιέται μαζεμένη.`,
          ],
        },
      ],
    },
    {
      heading: "Βγάλτε τις δόσεις από τη συμφωνία",
      content: [
        `Εδώ αποτυγχάνει η παρακολούθηση, και αποτυγχάνει αθόρυβα: πληρώνεται μια προκαταβολή και η υπόλοιπη δέσμευση μένει μέσα σε ένα χαρτί μέχρι να γίνει επείγουσα.`,
        `Μόλις κλείσει ένας συνεργάτης, περάστε αμέσως όλες τις πληρωμές που ονομάζει η συμφωνία. Για την καθεμία κρατήστε:`,
        {
          type: "list",
          items: [
            `τον συνεργάτη στον οποίο ανήκει`,
            `μια περιγραφή που λέει ποια πληρωμή είναι`,
            `το ποσό`,
            `την ημερομηνία, από τη συμφωνία`,
            `την κατάστασή της: εκκρεμεί ή πληρώθηκε`,
            `την ημερομηνία που πληρώθηκε πραγματικά`,
            `τον τρόπο πληρωμής ή ένα αποδεικτικό, αν θα χρειαστεί να το ξαναβρείτε`,
            `μια σημείωση για ό,τι ασυνήθιστο την αφορά`,
          ],
        },
        `Κάθε ημερομηνία και κάθε ποσό βγαίνει από τη συμφωνία που κάνατε. Μη συμπληρώσετε δόσεις από ένα γενικό χρονοδιάγραμμα γάμου: οι όροι αλλάζουν από συνεργάτη σε συνεργάτη, και μια ημερομηνία που υποθέσατε είναι ημερομηνία που δεν σας χρωστά κανείς.`,
        `Μία γραμμή ανά πληρωμή, όχι ανά συνεργάτη. Έστω μια συμφωνία 3.000 ευρώ, πληρωτέα σε τρία μέρη, με το πρώτο ήδη πληρωμένο:`,
        {
          type: "steps",
          items: [
            `Πρώτη πληρωμή, 600 ευρώ: πληρώθηκε, με την ημερομηνία της.`,
            `Δεύτερη πληρωμή, 1.200 ευρώ: εκκρεμεί, με την ημερομηνία της.`,
            `Τρίτη πληρωμή, 1.200 ευρώ: εκκρεμεί, με την ημερομηνία της.`,
          ],
        },
        `Αυτό δίνει τους τρεις αριθμούς που μετράνε: 3.000 συμφωνημένα, 600 πληρωμένα, 2.400 υπόλοιπο. Και βάζει τις επόμενες πληρωμές μπροστά σας μήνες πριν γίνουν επείγουσες, δίπλα σε όλες τις άλλες — που είναι ο μόνος αξιόπιστος τρόπος να δείτε δύο μεγάλα υπόλοιπα να πέφτουν το ίδιο δεκαπενθήμερο.`,
        `Τα τρία μέρη δεν είναι κανόνας, ούτε κανένας άλλος αριθμός. Κάποιες συμφωνίες εξοφλούνται με το κλείσιμο, κάποιες σε δύο μέρη, κάποιες δένουν μια πληρωμή με ένα ορόσημο αντί για ημερομηνία. Ό,τι κι αν λέει η δική σας, δώστε στην κάθε πληρωμή τη δική της γραμμή: τα πληρωμένα και τα δεσμευμένα είναι δύο διαφορετικοί αριθμοί, και μόνο ο ένας θα σας εκπλήξει.`,
      ],
    },
    {
      heading: "Πληρωμένα, υπόλοιπο, επόμενη πληρωμή",
      content: [
        `Τέσσερις αριθμοί κάνουν το μεγαλύτερο μέρος της δουλειάς:`,
        {
          type: "list",
          items: [
            `σύνολο συμφωνημένων, σε όλες τις συμφωνίες που έχετε κλείσει`,
            `σύνολο πληρωμένων, δηλαδή όσα έχουν πραγματικά φύγει`,
            `υπόλοιπο, δηλαδή συμφωνημένα μείον πληρωμένα, ανά συνεργάτη και συνολικά`,
            `επόμενη πληρωμή, με το ποσό και την ημερομηνία της`,
          ],
        },
        `Οι τρεις πρώτοι σας λένε πού βρίσκεστε. Ο τέταρτος σας λέει τι να κάνετε.`,
        `Οι περισσότερες λίστες καταλήγουν ταξινομημένες ανά κατηγορία, γιατί έτσι φτιάχτηκαν. Πρακτικά είναι λάθος ταξινόμηση: ένα υπόλοιπο 400 ευρώ που πέφτει την επόμενη Παρασκευή σάς χρειάζεται περισσότερο από ένα υπόλοιπο 4.000 ευρώ σε πέντε μήνες.`,
        `Κρατήστε μία εικόνα ταξινομημένη κατά ημερομηνία πληρωμής, αγνοώντας τις κατηγορίες, και διαβάστε την από πάνω προς τα κάτω. Αυτό είναι μια λίστα με το τι πρέπει να γίνει, με τη σειρά.`,
      ],
    },
    {
      heading: "Όταν αλλάζει η συμφωνία, αλλάζει και η καταγραφή",
      content: [
        `Ελάχιστοι γάμοι γίνονται ακριβώς όπως συμφωνήθηκαν πρώτα. Τα πράγματα κινούνται:`,
        {
          type: "list",
          items: [
            `μια ώρα παραπάνω κάλυψης`,
            `περισσότεροι καλεσμένοι από όσους κοστολογήθηκαν`,
            `ένα αναβαθμισμένο πακέτο ή μια υπηρεσία που προστέθηκε αργά`,
            `επιπλέον εξοπλισμός, προσωπικό ή μεταφορά`,
            `αλλαγή στο πρόγραμμα, στην αίθουσα ή στον χώρο`,
            `κάτι που αφαιρέθηκε, και μια τιμή που πρέπει να πέσει μαζί του`,
          ],
        },
        `Το καθένα αλλάζει συνήθως δύο πράγματα: το συμφωνημένο σύνολο και τις δόσεις που βγήκαν από αυτό. Ενημερώστε και τα δύο, και σημειώστε τι άλλαξε και πότε.`,
        `Ο τρόπος που χαλάει είναι πολύ συγκεκριμένος: η νέα συμφωνία ζει σε μια συνομιλία ενώ η καταγραφή δείχνει τα παλιά νούμερα, και εβδομάδες αργότερα η καταγραφή είναι αυτό από το οποίο οργανώνουν όλοι.`,
        `Όπου η αλλαγή είναι ουσιαστική — χρήματα, ώρες, άτομα, ημερομηνίες — ζητήστε από τον συνεργάτη να την επιβεβαιώσει γραπτά, έστω με ένα σύντομο email.`,
      ],
    },
    {
      heading: "Κρατήστε το αποδεικτικό, όχι μόνο τον αριθμό",
      content: [
        `Μια καταγραφή λέει ότι μια πληρωμή έγινε. Δεν είναι η απόδειξη ότι έγινε.`,
        `Αξίζει να κρατάτε ολόκληρα:`,
        {
          type: "list",
          items: [
            `προσφορές και τιμολόγια`,
            `αποδείξεις και επιβεβαιώσεις πληρωμής`,
            `τα αποδεικτικά των καταθέσεων ή των εμβασμάτων`,
            `το υπογεγραμμένο συμφωνητικό και κάθε αναθεωρημένη εκδοχή του`,
            `την αλληλογραφία στην οποία συμφωνήθηκε ή άλλαξε κάτι`,
          ],
        },
        `Και όταν κάτι πληρώνεται με μετρητά, γράψτε το ίδιο σχολαστικά: ποσό, ημερομηνία, σε ποιον. Μια πληρωμή που δεν κατέγραψε κανείς είναι μια πληρωμή για την οποία θα υπάρξει συζήτηση.`,
        `Κρατήστε τα σε ένα σταθερό σημείο, που ελέγχετε εσείς και στο οποίο θα έχετε πρόσβαση και έναν χρόνο μετά. Η καταγραφή δείχνει προς αυτά αντί να τα αντικαθιστά: η καταγραφή είναι το ευρετήριο, τα έγγραφα είναι το αρχείο.`,
      ],
    },
    {
      heading: "Δείτε τακτικά τι έρχεται",
      content: [
        `Μια καταγραφή είναι τόσο ενημερωμένη όσο και η τελευταία φορά που την κοίταξε κάποιος. Ένας σύντομος, επαναλαμβανόμενος έλεγχος την κρατά ειλικρινή:`,
        {
          type: "list",
          items: [
            `κοιτάξτε τις επόμενες 30 μέρες αρκετά συχνά ώστε να μη σας εκπλήξει τίποτα`,
            `κοιτάξτε 60 ή 90 μέρες μπροστά για το σχήμα όσων έρχονται`,
            `σημειώστε μια πληρωμή ως πληρωμένη μόνο όταν έχει όντως ολοκληρωθεί, με την ημερομηνία`,
            `κυνηγήστε ό,τι πέρασε την ημερομηνία του ή δεν εξηγείται`,
            `ελέγξτε ότι κάθε αλλαγή που συμφωνήθηκε από τον προηγούμενο έλεγχο έχει περαστεί`,
          ],
        },
        `Πόσο συχνά εξαρτάται από το πόσα τρέχουν: μηνιαία στη μέση της οργάνωσης, εβδομαδιαία μόλις αρχίσουν να πέφτουν μαζί οι εξοφλήσεις.`,
        `Καμία καταγραφή δεν κυνηγά τίποτα για λογαριασμό σας. Ο έλεγχος είναι ο μηχανισμός· η καλή καταγραφή απλώς τον κάνει γρήγορο.`,
      ],
    },
    {
      heading: "Οι πληρωμές δεν είναι ο προϋπολογισμός σας",
      content: [
        `Τα δύο αρχεία μπερδεύονται συνεχώς, ενώ απαντούν σε διαφορετικές ερωτήσεις.`,
        { type: "subheading", text: "Η καταγραφή των συνεργατών" },
        `Ποιους έχετε κλείσει, τι συμφωνήσατε, τι κοστίζει, τι έχει πληρωθεί, τι μένει και τι πέφτει μετά. Ανά δέσμευση, και είναι αυτό που δουλεύετε από βδομάδα σε βδομάδα.`,
        { type: "subheading", text: "Ο προϋπολογισμός" },
        `Τι αναμένεται να κοστίσει ο γάμος ανά κατηγορία, και πώς πάει η δαπάνη σε σχέση με το πλάνο. Ανά κατηγορία, και είναι αυτό που δουλεύετε όταν αποφασίζετε τι αντέχετε.`,
        `Επικαλύπτονται σε ένα σημείο: ένα συμφωνημένο σύνολο είναι δεσμευμένο κόστος, είτε έχει πληρωθεί είτε όχι. Ένας προϋπολογισμός που τρέφεται μόνο από τις πληρωμές που έχουν ήδη γίνει υποτιμά αυτό που χρωστάτε.`,
        `Προκαταβολές 600 ευρώ απέναντι σε υπογεγραμμένες δεσμεύσεις 3.000 ευρώ φαίνονται άνετες, και δεν είναι.`,
        {
          type: "p",
          text: [
            `Ένα καλό σύστημα συνδέει τα δύο, αλλά η πλευρά των συνεργατών είναι αυτή που σας λέει τι να κάνετε αυτή την εβδομάδα. Ο `,
            {
              text: "Προϋπολογισμός γάμου: πώς να τον οργανώσετε",
              href: "/guides/how-to-create-wedding-budget",
            },
            ` καλύπτει την άλλη.`,
          ],
        },
      ],
    },
    {
      heading: "Ένας τελευταίος έλεγχος πριν τον γάμο",
      content: [
        `Τις τελευταίες εβδομάδες, περάστε την καταγραφή συνεργάτη προς συνεργάτη και επιβεβαιώστε ότι:`,
        {
          type: "list",
          items: [
            `η συμφωνία που έχετε είναι η τρέχουσα, μαζί με ό,τι άλλαξε μετά την υπογραφή`,
            `το υπόλοιπο είναι αυτό που νομίζετε, και συμφωνεί και ο συνεργάτης`,
            `κάθε τελική πληρωμή έχει γίνει ή έχει προγραμματιστεί, στην ημερομηνία που ονομάζει η συμφωνία`,
            `κάθε ποσότητα που εξαρτάται από τον αριθμό ατόμων έχει επιβεβαιωθεί, μέχρι τη δική του προθεσμία`,
            `οι ώρες άφιξης, στησίματος και αποχώρησης έχουν συμφωνηθεί`,
            `κάθε ανοιχτή ερώτηση έχει κλείσει`,
            `έχετε ένα τηλέφωνο ανά συνεργάτη που θα απαντηθεί την ημέρα`,
            `όποιος τρέχει την ημέρα έχει την ίδια πληροφορία με εσάς`,
          ],
        },
        `Το πότε θα το κάνετε το ορίζουν οι προθεσμίες μέσα στις δικές σας συμφωνίες — κάποιοι θέλουν τελικούς αριθμούς εβδομάδες πριν, κάποιοι λίγες μέρες. Μετρήστε ανάποδα από αυτήν που έρχεται πρώτη, όχι από μια ημερομηνία που διάλεξε το checklist κάποιου άλλου.`,
      ],
    },
    {
      heading: "Τα συνηθισμένα λάθη",
      content: [
        `Τα περισσότερα από όσα πάνε στραβά είναι συνηθισμένα και όχι δραματικά:`,
        {
          type: "list",
          items: [
            `να μένουν όλα στα email, όπου τίποτα δεν αθροίζεται`,
            `να καταγράφεται η προκαταβολή και ποτέ το υπόλοιπο πίσω της`,
            `να μπαίνουν ημερομηνίες από ένα γενικό χρονοδιάγραμμα αντί από τη συμφωνία`,
            `να παρακολουθείται ο συνολικός προϋπολογισμός αλλά όχι το πότε πέφτει η κάθε πληρωμή`,
            `να συμφωνείται μια αλλαγή και η καταγραφή να μένει στα παλιά νούμερα`,
            `να σημειώνεται μια πληρωμή ως πληρωμένη ενώ απλώς κανονίστηκε`,
            `να βασίζεστε στη μνήμη για ώρες άφιξης, τι περιλαμβάνεται και τι πρόσβαση χρειάζεται ο καθένας`,
            `να φτάνετε στις τελευταίες εβδομάδες χωρίς να έχετε επιβεβαιώσει τίποτα`,
          ],
        },
        `Σχεδόν όλα έχουν μία αιτία: η καταγραφή συμπληρώθηκε όταν φτιάχτηκε και όχι όταν άλλαξαν τα πράγματα. Μια καταγραφή που έχετε πάψει να εμπιστεύεστε είναι το ίδιο με καμία.`,
      ],
    },
    {
      heading: "Όλοι οι συνεργάτες σε ένα σημείο",
      content: [
        {
          type: "note",
          text: `Ο χώρος οργάνωσης και ο Ἔρως στο Agapaé περιλαμβάνονται στο πακέτο Heirloom.`,
        },
        `Στο Agapaé η οργάνωση είναι χωρισμένη στα μέρη από τα οποία αποτελείται ένας γάμος, και το καθένα κρατά τους δικούς του συνεργάτες, εργασίες, κόστη, έγγραφα και ανοιχτές αποφάσεις. Κάθε συνεργάτης έχει τη δική του καρτέλα — το άτομο με το οποίο μιλάτε, τα στοιχεία του, τις σημειώσεις σας — και μια κατάσταση που ακολουθεί την πραγματική συζήτηση, από την έρευνα μέχρι το κλείσιμο ή την απόρριψη, με τα τηλεφωνήματα, τα email και τα ραντεβού καταγεγραμμένα από κάτω.`,
        `Τα κόστη καταγράφονται περίπου όπως τα περιγράφει αυτός ο οδηγός: μια περιγραφή, ένα ποσό, μια ημερομηνία και ο συνεργάτης στον οποίο ανήκουν, με το καθένα είτε να εκκρεμεί είτε να έχει πληρωθεί. Τέσσερα σύνολα μένουν μπροστά σας — συνολικό κόστος, πληρωμένα, οφειλόμενα και υπόλοιπο προϋπολογισμού — και ό,τι πέρασε την ημερομηνία του επισημαίνεται. Ό,τι έχει ημερομηνία εμφανίζεται στο ημερολόγιο, μαζί με τις πληρωμές, και συμβόλαια, προσφορές και αποδείξεις μπαίνουν ως αρχεία στον συνεργάτη από τον οποίο ήρθαν.`,
        `Ο Ἔρως ζει μέσα σε αυτόν τον χώρο: διαβάζει ό,τι υπάρχει και απαντά από αυτό, και όταν του το ζητήσετε κάνει ο ίδιος την αλλαγή — καταγράφει μια πληρωμή, ενημερώνει έναν συνεργάτη, προσθέτει την εργασία που ακολουθεί. Προτείνει και αποφασίζετε εσείς.`,
        `Δεν διαλέγει κανέναν για λογαριασμό σας. Το Agapaé δεν είναι κατάλογος επαγγελματιών, δεν επικοινωνεί, δεν κλείνει και δεν πληρώνει κανέναν μέσα από αυτό, και δεν στέλνει υπενθυμίσεις για τις πληρωμές των συνεργατών σας. Δεν είναι λογιστικό πρόγραμμα, δεν εκδίδει τιμολόγια, και δεν αντικαθιστά ούτε τα υπογεγραμμένα σας συμφωνητικά ούτε τη δική σας καταγραφή για το τι έχετε πληρώσει.`,
        {
          type: "p",
          text: [
            `Η οργάνωση περιγράφεται στη σελίδα `,
            { text: "Οργάνωση γάμου", href: "/wedding-planning" },
            `. Οι συνεργάτες είναι ένα κομμάτι ενός μεγαλύτερου πλάνου, και το `,
            {
              text: "Checklist γάμου: τι να οργανώσετε και πότε",
              href: "/guides/wedding-planning-checklist",
            },
            ` καλύπτει τη σειρά με την οποία γίνονται τα υπόλοιπα.`,
          ],
        },
      ],
    },
  ],

  faqHeading: "Συχνές ερωτήσεις",

  faq: [
    {
      q: `Τι πρέπει να κρατάω για κάθε συνεργάτη;`,
      a: `Μία εγγραφή ανά συνεργάτη: επωνυμία, κατηγορία, το άτομο με το οποίο μιλάτε και τα στοιχεία του, την κατάσταση του κλεισίματος, αν υπάρχει υπογεγραμμένο συμφωνητικό, το συμφωνημένο σύνολο, τα πληρωμένα, το υπόλοιπο και την επόμενη πληρωμή με την ημερομηνία της. Δίπλα τους τα πρακτικά: ώρα άφιξης, τι περιλαμβάνεται, τι μένει αναπάντητο.`,
    },
    {
      q: `Πώς παρακολουθώ τις πληρωμές;`,
      a: `Βγάλτε τις δόσεις από κάθε συμφωνία μόλις κλείσει, και δώστε στην κάθε πληρωμή τη δική της γραμμή με τον συνεργάτη, μια περιγραφή, το ποσό, την ημερομηνία και μια κατάσταση. Σημειώστε πληρωμένη μόνο όταν έχει όντως ολοκληρωθεί. Κρατήστε τρία σύνολα ανά συνεργάτη και μία εικόνα με όλες τις απλήρωτες, ταξινομημένες κατά ημερομηνία.`,
    },
    {
      q: `Να κρατάω χωριστά προκαταβολή και εξόφληση;`,
      a: `Ναι. Ένας ενιαίος αριθμός «πληρωμένα» λέει ότι κάτι τακτοποιήθηκε, χωρίς να λέει τι έρχεται και πότε. Αν μια συμφωνία έχει περισσότερες από μία πληρωμές, δώστε στην καθεμία δική της ημερομηνία και κατάσταση, ώστε ένα υπόλοιπο να φαίνεται μήνες πριν γίνει επείγον.`,
    },
    {
      q: `Πότε πληρώνονται συνήθως οι συνεργάτες;`,
      a: `Εξαρτάται εξ ολοκλήρου από τον συνεργάτη και τη συμφωνία, και δεν πρέπει ποτέ να συμπεραίνεται από ένα γενικό χρονοδιάγραμμα. Οι όροι αλλάζουν ανά συνεργάτη, εποχή και συμφωνία. Βάλτε τις ημερομηνίες και τα ποσά που γράφει η συμφωνία που κάνατε, και επιβεβαιώστε τα αν κάτι είναι ασαφές.`,
    },
    {
      q: `Φτάνει ένα Excel;`,
      a: `Για πολλά ζευγάρια ναι. Ένα αρχείο κρατά μια χαρά τις γραμμές των συνεργατών και των πληρωμών, αρκεί να είναι το μοναδικό και να ενημερώνεται όταν αλλάζουν τα πράγματα. Ένα εργαλείο οργάνωσης βοηθά κυρίως στο να μένουν συμφωνητικά, πληρωμές, ιστορικό και εργασίες δεμένα στον ίδιο συνεργάτη αντί για τέσσερα διαφορετικά σημεία.`,
    },
  ],

  related: {
    heading: "Πού ταιριάζει αυτό στο Agapaé",

    guides: ["wedding-planning-checklist", "how-to-create-wedding-budget"],

    product: [
      {
        href: "/wedding-planning",
        label: "Οργάνωση γάμου",
        blurb:
          "Δείτε πώς ο χώρος οργάνωσης κρατά τους συνεργάτες σας, τα συμφωνητικά τους, τι τους οφείλετε και τι έχετε πληρώσει σε ένα ιδιωτικό σημείο, δίπλα στις εργασίες και τα έγγραφά σας. Διαθέσιμο στο πακέτο Heirloom.",
      },
    ],
  },
};

export default howToTrackWeddingVendorsPaymentsEl;
